import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import logoOnNavy from "@/assets/images/logo-main-on-navy.svg";
import { makeBursts } from "../../utils/fireworks";
import { IntroAudio, startIntroAudio } from "../../utils/introAudio";
import { LOGO_DURATION, logoFrame } from "../../utils/logoSequence";
import "./PreEpWelcomeIntro.css";

/** Length of the steps phase, in seconds (CSS timeline in PreEpWelcomeIntro.css). */
const STEPS_DURATION = 41.5;
/** Where "Next" jumps to on the steps timeline: four-steps title, steps 1–4, final beat. */
const SCENE_STOPS = [2.8, 6.8, 14.8, 22.8, 30.8, 38.9];

const STEPS = [
  {
    scene: "pep-s3",
    at: 7.1,
    eyebrow: "Start here",
    title: "Complete your Culture Index Profile",
    body: "A quick survey that helps us match you with the right coaches.",
  },
  {
    scene: "pep-s4",
    at: 15.1,
    eyebrow: "In person or virtual",
    title: "Register for Executive Perspective",
    body: "Work on your business with CertainPath experts and other owners.",
  },
  {
    scene: "pep-s5",
    at: 23.1,
    eyebrow: "Group welcome session",
    title: "Register for a CertainPath Group Welcome Session",
    body: "Get oriented and ask any initial questions, so you head into EP confident and connected.",
  },
  {
    scene: "pep-s6",
    at: 31.1,
    eyebrow: "Recommended Before EP",
    title: "Complete your CertainPath Activation Course",
    body: "Learn the foundations, so you arrive at EP ready to build.",
  },
];

const CAPTIONS = [
  "Get ready.",
  "We’re glad you’re here.",
  "Your path starts here.",
];

type Phase = "logo" | "steps" | "none";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;

const controlClass =
  "inline-flex min-h-[44px] items-center gap-2 rounded-full border-0 bg-transparent px-4 text-[15px] font-bold text-[#C3CBE0] cursor-pointer";

interface PreEpWelcomeIntroProps {
  firstName: string;
  /** Finished, skipped, or advanced past the end. Called once. */
  onDone: () => void;
}

/**
 * The full-screen welcome a new Pre-EP member sees on their first home page
 * visit (about 53 seconds): a logo sequence, then the four steps to get ready
 * for EP, with fireworks. Skip, Next, Replay, and a sound toggle (starts off).
 */
const PreEpWelcomeIntro: React.FC<PreEpWelcomeIntroProps> = ({
  firstName,
  onDone,
}) => {
  const reduced = useMemo(prefersReducedMotion, []);
  const bursts = useMemo(makeBursts, []);
  const [phase, setPhase] = useState<Phase>(reduced ? "steps" : "logo");
  const [run, setRun] = useState(0);
  const [sound, setSound] = useState(false);
  const [blocked, setBlocked] = useState(false);

  const seqStart = useRef(performance.now());
  const audio = useRef<IntroAudio | null>(null);
  const endTimer = useRef<number | undefined>(undefined);
  const unlockedAt = useRef(0);
  const done = useRef(false);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  const stepsRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const slideRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const captionRefs = useRef<(HTMLParagraphElement | null)[]>([]);
  const ecgRef = useRef<SVGPathElement>(null);
  const lineRef = useRef<SVGPathElement>(null);
  const markRef = useRef<SVGGElement>(null);
  const nameRef = useRef<SVGGElement>(null);
  const clipRef = useRef<SVGRectElement>(null);

  const elapsed = () => (performance.now() - seqStart.current) / 1000;

  const stopAudio = useCallback(() => {
    audio.current?.stop();
    audio.current = null;
  }, []);

  const startAudio = useCallback(
    (offset: number) => {
      stopAudio();
      audio.current = startIntroAudio(
        offset,
        reduced,
        bursts.map((b) => b.at),
        () => {
          audio.current = null;
          setBlocked(true);
        },
      );
    },
    [bursts, reduced, stopAudio],
  );

  const finish = useCallback(() => {
    if (done.current) return;
    done.current = true;
    window.clearTimeout(endTimer.current);
    stopAudio();
    onDoneRef.current();
  }, [stopAudio]);

  const endAfter = useCallback(
    (ms: number) => {
      window.clearTimeout(endTimer.current);
      endTimer.current = window.setTimeout(finish, ms);
    },
    [finish],
  );

  // Starts muted; the sound toggle turns it on mid-intro.
  useEffect(() => {
    seqStart.current = performance.now();
    return () => {
      window.clearTimeout(endTimer.current);
      stopAudio();
    };
  }, [startAudio, stopAudio]);

  // The steps phase ends on its own after STEPS_DURATION.
  useEffect(() => {
    if (phase === "steps") endAfter(STEPS_DURATION * 1000);
  }, [phase, run, endAfter]);

  // The logo sequence, frame by frame.
  useEffect(() => {
    if (phase !== "logo") return undefined;
    const start = performance.now();
    let raf = 0;
    const frame = (now: number) => {
      const t = (now - start) / 1000;
      const f = logoFrame(
        t,
        slideRef.current?.getBoundingClientRect().width ?? 760,
      );
      const line = lineRef.current;
      const ecg = ecgRef.current;
      if (line && ecg) {
        ecg.setAttribute("d", f.ecg.d);
        ecg.setAttribute("opacity", f.ecg.visible ? "1" : "0");
        line.setAttribute("d", f.line.d);
        if (f.line.dash) {
          line.setAttribute("stroke-dasharray", f.line.dash.array);
          line.setAttribute("stroke-dashoffset", String(f.line.dash.offset));
        } else {
          line.removeAttribute("stroke-dasharray");
          line.removeAttribute("stroke-dashoffset");
        }
        line.setAttribute(
          "transform",
          `translate(208 300) scale(${f.line.scale.toFixed(3)}) translate(-208 -300)`,
        );
        line.style.filter =
          f.line.pulse > 0.01
            ? `drop-shadow(0 0 ${(30 * f.line.pulse).toFixed(1)}px rgba(255,60,90,${(0.85 * f.line.pulse).toFixed(2)}))`
            : "";
        line.setAttribute("opacity", String(f.line.opacity));
      }
      markRef.current?.setAttribute("opacity", String(f.mark.opacity));
      markRef.current?.setAttribute(
        "transform",
        `translate(208 292) scale(${f.mark.scale.toFixed(3)}) translate(-208 -292)`,
      );
      if (slideRef.current) {
        slideRef.current.style.transform = `translateX(${f.slideX.toFixed(1)}px)`;
      }
      clipRef.current?.setAttribute("width", String(f.nameClipWidth));
      nameRef.current?.setAttribute(
        "transform",
        `translate(${f.nameShiftX.toFixed(1)} 0)`,
      );
      const fadeTo = (
        el: HTMLElement | null | undefined,
        v: { opacity: number; translateY: number },
      ) => {
        if (!el) return;
        el.style.opacity = String(v.opacity);
        el.style.transform = `translateY(${v.translateY.toFixed(1)}px)`;
      };
      fadeTo(titleRef.current, f.title);
      f.captions.forEach((c, i) => fadeTo(captionRefs.current[i], c));
      fadeTo(boxRef.current, f.box);

      if (t < LOGO_DURATION) raf = requestAnimationFrame(frame);
      else setPhase("steps");
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [phase, run]);

  const resyncAudio = () => {
    if (sound && !blocked && audio.current) startAudio(elapsed());
  };

  const next = () => {
    if (phase === "logo") {
      seqStart.current = performance.now() - LOGO_DURATION * 1000;
      setPhase("steps");
      resyncAudio();
      return;
    }
    const root = stepsRef.current;
    if (!root?.getAnimations) return;
    const anims = root.getAnimations({ subtree: true });
    const bar = anims.find(
      (a) => (a as CSSAnimation).animationName === "pepBar",
    );
    const now = bar?.currentTime != null ? Number(bar.currentTime) / 1000 : 0;
    const target = SCENE_STOPS.find((s) => s > now + 0.3);
    if (target === undefined) {
      finish();
      return;
    }
    const jump = target - now;
    anims.forEach((a) => {
      try {
        a.currentTime = Number(a.currentTime ?? 0) + jump * 1000;
      } catch {
        // an animation that already finished can't be moved; ignore it
      }
    });
    seqStart.current -= jump * 1000;
    endAfter(Math.max(0, (STEPS_DURATION - target) * 1000));
    resyncAudio();
  };

  const replay = () => {
    stopAudio();
    window.clearTimeout(endTimer.current);
    setPhase("none");
    window.setTimeout(() => {
      seqStart.current = performance.now();
      setRun((r) => r + 1);
      setPhase(reduced ? "steps" : "logo");
      if (sound) startAudio(0);
    }, 60);
  };

  const unlock = () => {
    if (sound && blocked) {
      unlockedAt.current = performance.now();
      setBlocked(false);
      startAudio(elapsed());
    }
  };

  const toggleSound = () => {
    // A click that just unlocked the sound shouldn't turn it right off again.
    if (performance.now() - unlockedAt.current < 400) return;
    if (sound) {
      stopAudio();
      setSound(false);
      setBlocked(false);
    } else {
      setSound(true);
      setBlocked(false);
      startAudio(elapsed());
    }
  };

  const soundBlocked = sound && blocked;
  const soundLabel = soundBlocked
    ? "Click for sound"
    : sound
      ? "Sound on"
      : "Sound off";

  return (
    <div
      className="fixed inset-0 z-[100] overflow-hidden bg-[#0A1430] text-white"
      data-testid="pre-ep-welcome-intro"
      onPointerDown={unlock}
    >
      <div aria-hidden="true" className="pep-glow pep-glow-a" />
      <div aria-hidden="true" className="pep-glow pep-glow-b" />

      {soundBlocked && (
        <div className="pointer-events-none absolute inset-x-0 bottom-24 z-[4] sm:bottom-8 flex justify-center">
          <span className="inline-flex min-h-[36px] items-center gap-2 rounded-full border border-[#2C3A66] bg-[rgba(17,29,64,.85)] px-4 text-sm font-bold text-[#E6EAF5]">
            <SpeakerIcon on size={16} />
            Click anywhere to turn on sound
          </span>
        </div>
      )}

      {/* Top bar: brand + controls */}
      <div className="absolute inset-x-0 top-0 z-[3] flex min-h-[44px] items-center justify-between gap-2 px-4 py-4 sm:gap-4 sm:px-8 sm:py-6">
        <div className="min-h-[30px]">
          {phase === "steps" && (
            <img
              alt="CertainPath"
              className="block h-6 w-auto sm:h-[30px]"
              src={logoOnNavy}
            />
          )}
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <button
            aria-pressed={sound}
            className={controlClass}
            onClick={toggleSound}
            type="button"
          >
            <SpeakerIcon on={sound} size={18} />
            {/* Icon-only on phones; the label stays for screen readers. */}
            <span className="sr-only sm:not-sr-only">{soundLabel}</span>
          </button>
          <button className={controlClass} onClick={replay} type="button">
            <svg
              aria-hidden="true"
              fill="none"
              height="16"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2.2"
              viewBox="0 0 24 24"
              width="16"
            >
              <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
              <path d="M3 3v5h5" />
            </svg>
            <span className="sr-only sm:not-sr-only">Replay</span>
          </button>
          <button
            className="inline-flex min-h-[44px] items-center whitespace-nowrap rounded-full border border-[#3A4A7E] bg-transparent px-4 text-[15px] font-bold text-white cursor-pointer hover:text-[#FFD7E0] sm:px-[18px]"
            onClick={finish}
            type="button"
          >
            Skip intro
          </button>
        </div>
      </div>

      {!reduced && phase !== "none" && (
        <button
          aria-label="Next step"
          className="absolute bottom-5 right-4 z-[3] sm:bottom-7 sm:right-8 inline-flex min-h-[44px] items-center gap-2 rounded-full border border-[#3A4A7E] bg-[rgba(12,20,48,.55)] pl-5 pr-[18px] text-[15px] font-bold text-white cursor-pointer transition-colors hover:border-[#5A6BA0] hover:bg-[#1B274B] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FF8AA3]"
          onClick={next}
          type="button"
        >
          Next <ArrowIcon />
        </button>
      )}

      {/* Logo sequence: line → heart → paperclip mark → CertainPath */}
      {phase === "logo" && (
        <div
          aria-label="CertainPath"
          className="absolute inset-0 z-[2] flex items-center justify-center"
          key={`logo-${run}`}
          ref={boxRef}
          role="img"
        >
          <h1
            className="pep-hero absolute inset-x-0 m-0 px-6 text-center font-light tracking-[0.01em] opacity-0"
            ref={titleRef}
            style={{ bottom: "calc(50% + 150px)" }}
          >
            Welcome, {firstName}
          </h1>
          <div ref={slideRef}>
            <LogoSvg
              clipRef={clipRef}
              ecgRef={ecgRef}
              lineRef={lineRef}
              markRef={markRef}
              nameRef={nameRef}
            />
          </div>
          {CAPTIONS.map((caption, i) => (
            <p
              className="absolute inset-x-0 m-0 px-6 text-center text-2xl font-light text-[#E6EAF5] opacity-0 sm:text-[34px]"
              key={caption}
              ref={(el) => {
                captionRefs.current[i] = el;
              }}
              style={{ top: "calc(50% + 140px)" }}
            >
              {caption}
            </p>
          ))}
        </div>
      )}

      {phase === "steps" && (
        <div
          className="pep-play absolute inset-0 z-[2]"
          key={`steps-${run}`}
          ref={stepsRef}
        >
          {/* Fireworks */}
          <div aria-hidden="true" className="pep-fw">
            {bursts.map((b) => (
              <React.Fragment key={b.delay + b.x}>
                <span className="pep-rocket" style={{ left: b.x, top: b.y }}>
                  <span
                    className="pep-rocket-head"
                    style={{ animationDelay: b.launch }}
                  />
                </span>
                <span
                  className="pep-burst"
                  style={{ left: b.x, top: b.y, animationDelay: b.delay }}
                >
                  <span
                    className="pep-flash"
                    style={{ background: b.glow, animationDelay: b.delay }}
                  />
                  {b.rays.map((r, i) => (
                    <span
                      className="pep-ray"
                      key={i}
                      style={{ transform: `rotate(${r.angle})` }}
                    >
                      <span
                        className={r.className}
                        style={{
                          background: r.color,
                          boxShadow: `0 0 5px 1px ${r.color}`,
                          animationDelay: r.delay,
                          animationDuration: r.duration,
                        }}
                      />
                    </span>
                  ))}
                </span>
              </React.Fragment>
            ))}
          </div>

          {/* Four steps */}
          <section aria-hidden="true" className="pep-scene pep-s2">
            <div className="flex max-w-[820px] flex-col items-center gap-[22px] text-center">
              <h2
                className="pep-hero pep-rise m-0 font-light leading-[1.15]"
                style={{ animationDelay: "3.2s" }}
              >
                Four steps to get ready for your{" "}
                <span className="font-bold">Executive Perspective</span> week.
              </h2>
              <div
                className="pep-rise flex gap-3"
                style={{ animationDelay: "3.8s" }}
              >
                {[0, 1, 2, 3].map((i) => (
                  <span
                    className={`h-1.5 w-11 rounded-[3px] ${i === 0 ? "bg-[#E0103F]" : "bg-[#3A4A7E]"}`}
                    key={i}
                  />
                ))}
              </div>
            </div>
          </section>

          {STEPS.map((step, i) => (
            <section
              aria-hidden="true"
              className={`pep-scene ${step.scene}`}
              key={step.scene}
            >
              <div className="flex max-w-[1040px] flex-wrap items-center justify-center gap-6 text-center sm:gap-12 sm:text-left">
                <span
                  className="pep-num flex h-[88px] w-[88px] flex-none items-center justify-center rounded-full bg-[#E0103F] text-[40px] font-extrabold sm:h-[150px] sm:w-[150px] sm:text-[72px]"
                  style={{ animationDelay: `${step.at}s` }}
                >
                  {i + 1}
                </span>
                <div className="flex min-w-0 flex-[1_1_440px] flex-col items-center gap-3 sm:items-start sm:gap-3.5">
                  <span
                    className="pep-rise text-[15px] font-extrabold uppercase tracking-[0.1em] text-[#FF8AA3]"
                    style={{ animationDelay: `${step.at + 0.3}s` }}
                  >
                    {step.eyebrow}
                  </span>
                  <h2
                    className="pep-step-title pep-rise m-0 font-bold leading-[1.08]"
                    style={{ animationDelay: `${step.at + 0.5}s` }}
                  >
                    {step.title}
                  </h2>
                  <p
                    className="pep-rise m-0 text-lg leading-normal text-[#D3D9EA] sm:text-[22px]"
                    style={{ animationDelay: `${step.at + 0.9}s` }}
                  >
                    {step.body}
                  </p>
                </div>
              </div>
            </section>
          ))}

          {/* Final beat */}
          <section aria-hidden="true" className="pep-scene pep-final">
            <h2 className="m-0 text-[40px] font-light sm:text-[64px]">
              Let&rsquo;s get started.
            </h2>
          </section>

          {/* Progress */}
          <div
            aria-hidden="true"
            className="absolute inset-x-0 bottom-0 h-1 bg-[#1E2A4F]"
          >
            <div className="pep-bar h-1 w-0 bg-[#E0103F]" />
          </div>
        </div>
      )}
    </div>
  );
};

const SpeakerIcon: React.FC<{ on: boolean; size: number }> = ({ on, size }) => (
  <svg
    aria-hidden="true"
    fill="none"
    height={size}
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2.1"
    viewBox="0 0 24 24"
    width={size}
  >
    <path d="M4 9v6h4l5 4V5L8 9z" />
    {on ? (
      <path d="M16.5 8.5a5 5 0 0 1 0 7M19 6a8.5 8.5 0 0 1 0 12" />
    ) : (
      <path d="M17 9l5 6M22 9l-5 6" />
    )}
  </svg>
);

const ArrowIcon: React.FC = () => (
  <svg
    aria-hidden="true"
    fill="none"
    height="16"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2.2"
    viewBox="0 0 24 24"
    width="16"
  >
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

interface LogoSvgProps {
  ecgRef: React.RefObject<SVGPathElement>;
  lineRef: React.RefObject<SVGPathElement>;
  markRef: React.RefObject<SVGGElement>;
  nameRef: React.RefObject<SVGGElement>;
  clipRef: React.RefObject<SVGRectElement>;
}

/** The animated logo: the moving line, the paperclip mark and the "CertainPath" name. */
const LogoSvg: React.FC<LogoSvgProps> = ({
  ecgRef,
  lineRef,
  markRef,
  nameRef,
  clipRef,
}) => (
  <svg aria-hidden="true" className="pep-logo-svg" viewBox="0 0 2676.68 583.97">
    <defs>
      <clipPath id="pepNameClip">
        <rect height="584" ref={clipRef} width="0" x="460" y="0" />
      </clipPath>
    </defs>
    <path
      d="M-800 292"
      fill="none"
      opacity="0"
      ref={ecgRef}
      stroke="#D0213F"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="40"
    />
    <path
      d="M160 292"
      fill="none"
      ref={lineRef}
      stroke="#D0213F"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="44"
    />
    <g fill="#B21E34" opacity="0" ref={markRef}>
      <path d="M209.62,548.06,378.84,378.84a123.53,123.53,0,0,0,14.53-17.54c.33-.48.67-.95,1-1.43.44-.66.85-1.34,1.27-2,.72-1.13,1.44-2.25,2.11-3.39l.21-.36a122.88,122.88,0,0,0-19.11-149L209.62,35.91A122.79,122.79,0,0,0,36,209.56L75.5,249.1a23.89,23.89,0,0,0,33.78-33.78L69.74,175.78A75,75,0,1,1,175.84,69.69l100.6,100.6a122.2,122.2,0,0,0-71.25,34.89L36,374.41A122.79,122.79,0,1,0,209.62,548.06ZM69.74,514.29a75,75,0,0,1,0-106.1L239,239a75,75,0,1,1,0,106,23.89,23.89,0,1,0-33.78,33.78,122.23,122.23,0,0,0,71.26,34.89L175.84,514.29a75,75,0,0,1-106.1,0Z" />
    </g>
    <g clipPath="url(#pepNameClip)">
      <g ref={nameRef}>
        <g fill="#FFFFFF">
          <path d="M615.3,427.75q-41.53,0-69.73-18.44t-42.49-51.09q-14.290-32.64-14.29-75.32t14.29-75.33q14.28-32.64,42.49-51.09T615.3,138q48.47,0,78.7,25.11t41,67.21l-23,5.6q-8.88-35-33.12-55.53T615.3,159.86q-34.2,0-56.88,15.65T524.23,219q-11.51,27.82-11.69,63.94-.39,36.12,11.11,63.83t34.48,43.46q23,15.75,57.17,15.74,39.39,0,63.64-20.67t33.12-55.43l23,5.6q-10.83,42.1-41,67.22T615.3,427.75Z" />
          <path d="M863.48,427.75q-29.94,0-51.76-13.42t-33.8-38.05q-12-24.63-12-58.23,0-34.18,11.87-59t33.61-38.15q21.74-13.32,51.67-13.32,30.31,0,51.86,13.81t32.83,39.88q11.3,26.08,10.72,62.78H935.33v-7.73q-1-42.88-19.31-65.28t-52.54-22.41q-35.34,0-54.56,23.28T789.7,317.66q0,42.12,19.22,65.28t54.18,23.18q24.14,0,42.2-11.3T934,362.47l20.09,8.89q-12.55,27-36.31,41.71T863.48,427.75ZM781.21,324V304H946V324Z" />
          <path d="M1001.19,422V213.36h20.28v50l-5-6.57a79.77,79.77,0,0,1,8.79-16.51,66.54,66.54,0,0,1,10.91-12.46,55.41,55.41,0,0,1,19.51-11.69,82.46,82.46,0,0,1,22.6-4.63,64.37,64.37,0,0,1,20.66,1.83v21.06a73.83,73.83,0,0,0-26.26-1.16q-13.9,1.74-26.08,12a55.23,55.23,0,0,0-15.45,21.54,96,96,0,0,0-6.18,25.69,261.52,261.52,0,0,0-1.35,26.17V422Z" />
          <path d="M1124.59,233.64V213.36h85.75v20.28ZM1210.34,422a121.33,121.33,0,0,1-32.83,2.8,71,71,0,0,1-28.88-7.34,41.61,41.61,0,0,1-19-20,58.65,58.65,0,0,1-5.8-22.21q-.57-11.21-.58-25.69V155.42h22.41V349.14q0,13.14.29,21.73a36.65,36.65,0,0,0,4,15.75q6.94,13.32,22,16.12t38.44-.86Z" />
          <path d="M1310.59,427.75q-24.15,0-40.09-8.49t-23.85-22.51a60.71,60.71,0,0,1-7.92-30.41q0-17.77,7.44-29.75a57.34,57.34,0,0,1,19.89-19.32,100.82,100.82,0,0,1,27.53-11q17.19-3.87,37.08-6.67t38-4.92q18-2.13,29.45-3.67l-8.11,4.83q.77-33.79-12.65-50.32T1330.09,229q-23.76,0-39.11,10.72t-21.54,33.12l-22.79-6.37q7.14-28.4,28.78-43.65t55.44-15.26q28.77,0,48.38,11.2t27.33,31.49a73.77,73.77,0,0,1,4.44,19.41,215.42,215.42,0,0,1,1.16,22.3V422H1391.9V367.11l7.53,1.93q-9.47,28.2-33,43.46T1310.59,427.75Zm1.15-20.47q21.24,0,37.28-7.63a66.77,66.77,0,0,0,26-21.44q9.95-13.8,12.65-32.54a137.21,137.21,0,0,0,2.12-22.6q.2-12.17.2-18l9.65,6.18q-12.55,1.54-30,3.28T1334,318.82a283.17,283.17,0,0,0-32.84,6.37,95.58,95.58,0,0,0-18.25,7.25,43.14,43.14,0,0,0-15.26,13q-6.09,8.31-6.08,21.06a41.93,41.93,0,0,0,4.73,18.93q4.73,9.46,15.74,15.64T1311.74,407.28Z" />
          <path d="M1466.26,171.26V142.87h22.21v28.39Zm0,250.7V213.36h22.21V422Z" />
          <path d="M1546.41,422V213.36h20.28V260.3h2.13V422Zm152.78,0V313.41a142.06,142.06,0,0,0-4-35.15q-4-15.45-12-26.27a51.84,51.84,0,0,0-20.09-16.41q-12.08-5.61-28.49-5.6-17,0-29.46,5.89a54.19,54.19,0,0,0-20.47,16.51,72.87,72.87,0,0,0-12,25.2,121.06,121.06,0,0,0-4,31.780l-17.58-2.13q0-33.8,11.49-55.72t31-32.45q19.51-10.53,43.85-10.52a88.56,88.56,0,0,1,30.8,5A71.21,71.21,0,0,1,1692,227.75a78.55,78.55,0,0,1,16.61,21.64,105.91,105.91,0,0,1,9.76,27.32,148.41,148.41,0,0,1,3.19,31.1V422Z" />
        </g>
        <g fill="#B21E34">
          <path d="M1771.81,422V143.83h112.41c2.71,0,6,.1,10,.29a91.61,91.61,0,0,1,11.4,1.25q23.17,3.67,38.73,15.85a74.88,74.88,0,0,1,23.27,30.7,105.5,105.5,0,0,1,7.73,41q0,22.6-7.73,41.14a74.91,74.91,0,0,1-23.27,30.71q-15.56,12.16-38.73,15.84-5.42.76-11.49,1.16c-4.06.26-7.38.38-10,.38h-72V422Zm40.37-138.1h70.5c2.57,0,5.43-.12,8.59-.39a62.09,62.09,0,0,0,9-1.35q12.36-2.89,19.9-10.71A46,46,0,0,0,1931,253.34a66.61,66.61,0,0,0,3.29-20.47A65.75,65.75,0,0,0,1931,212.5a46.14,46.14,0,0,0-10.81-18q-7.53-7.82-19.9-10.72a49.79,49.79,0,0,0-9-1.54q-4.74-.39-8.59-.39h-70.5Z" />
          <path d="M2066.55,427.75q-23.18,0-38.82-8.59t-23.66-22.79a62.39,62.39,0,0,1-8-31.19q0-15.84,5.6-27.82A54.33,54.33,0,0,1,2018.27,317q11-8.42,27-13.62a299.63,299.63,0,0,1,31.48-7.15q17.58-3.09,37-5.79t38.53-5.41l-14.68,8.11q.39-24.53-10.33-36.41t-36.79-11.87a60.93,60.93,0,0,0-30.14,7.63q-13.71,7.64-19.12,25.2l-37.66-11.59q7.73-26.83,29.45-42.68t57.85-15.84q28,0,48.77,9.56a60.51,60.51,0,0,1,30.42,30.23,63.1,63.1,0,0,1,6.18,21.63,244,244,0,0,1,1.16,24.53V422h-35.73V374.25l6.95,6.18q-12.94,24-32.93,35.64T2066.55,427.75Zm7.15-33q17.19,0,29.55-6.09a57.2,57.2,0,0,0,19.9-15.54,49.07,49.07,0,0,0,9.85-19.71,71.07,71.07,0,0,0,3.76-20.86q.48-11.58.48-18.54l13.14,4.83q-19.12,2.9-34.77,5.22t-28.29,4.54a180.91,180.91,0,0,0-22.51,5.3,73.72,73.72,0,0,0-14.77,7,32.66,32.66,0,0,0-10.24,9.85,25.33,25.33,0,0,0-3.76,14.1,28.9,28.9,0,0,0,4,15,28.29,28.29,0,0,0,12.36,10.91Q2060.76,394.74,2073.7,394.73Z" />
          <path d="M2215.06,245.81V213.36h100v32.45ZM2315,422a172.9,172.9,0,0,1-38.63,3.28q-18.93-.57-33.8-7.34a46.06,46.06,0,0,1-22.4-21.24,61.13,61.13,0,0,1-7.24-26.37q-.49-13.41-.49-30.42V155.42H2253V337.94q0,12.56.29,21.83t4,15.64q7,12,22.12,13.62t35.63-1.26Z" />
          <path d="M2358.69,422V143.83h36.12V293.91h4.83V422Zm146.59,0V319.4a160.47,160.47,0,0,0-2-25,78.09,78.09,0,0,0-7.73-23.85,46.39,46.39,0,0,0-16-17.78q-10.34-6.75-26.94-6.76a58.58,58.58,0,0,0-20.48,3.58,42.76,42.76,0,0,0-16.9,11.49q-7.24,7.92-11.39,20.86t-4.15,31.48L2374.53,304q0-28.4,10.62-50.12a79.9,79.9,0,0,1,30.51-33.9q19.9-12.17,48.1-12.17,21.63,0,36.31,7A67,67,0,0,1,2523.92,233a84.72,84.72,0,0,1,14,24.62,127.63,127.63,0,0,1,6.56,26.08,168.22,168.22,0,0,1,1.74,22.4V422Z" />
          <path d="M2575.11,209.64V150H2552v-6.48h53V150h-23.1v59.67Z" />
          <path d="M2612.32,209.64V143.49h6.2l26,55.63,25.86-55.63h6.3v66.1h-6.44v-51l-23.56,51.08h-4.36l-23.52-51.08v51.08Z" />
        </g>
      </g>
    </g>
  </svg>
);

export default PreEpWelcomeIntro;
